import { Component } from 'react';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { isEqual } from 'underscore';
import { addValidationError, removeValidationError, shiftValidationError } from 'ContentData/actions/ValidationErrors';
import { makeGetValidationErrorIndexes } from 'ContentData/selectors/validationErrors';
import { getNewOccurrenceValue } from 'ContentComponents/components/cmv2/utils/occurrence';
import { groupHasAllInlineEditableChildren } from 'ContentComponents/components/cmv2/utils';
import { Small } from 'foundations-components/transitional/components/Small';
import { Table } from 'foundations-components/transitional/components/Table';
import { Text } from 'foundations-components/components/Text';
import OccurrencesTableRow from 'ContentComponents/components/cmv2/occurrences/OccurrencesTableRow';
import OccurrencesTableCell from './OccurrencesTableCell';
import OccurrenceLabel from 'ContentComponents/components/cmv2/occurrences/OccurrenceLabel';
import EditFormLabel from 'ContentComponents/components/cmv2/EditFormLabel';
import EditFormControl from 'ContentComponents/components/cmv2/EditFormControl';
import SanitizedLabel from 'ContentComponents/components/cmv2/SanitizedLabel';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { HUBSPOT_META_VALUE_KEY } from 'ContentComponents/components/cmv2/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `ContentComponents.cmv2.occurrence.${suffix}`;
function makeMapStateToProps() {
  const getValidationErrorIndexes = makeGetValidationErrorIndexes();
  return function mapStateToProps(state, props) {
    return {
      validationErrorIndexes: getValidationErrorIndexes(state, props)
    };
  };
}
const mapDispatchToProps = {
  addValidationError,
  removeValidationError,
  shiftValidationError
};
const connector = connect(makeMapStateToProps, mapDispatchToProps);
class OccurrencesTable extends Component {
  constructor(props) {
    super(props);
    this.hideEditButton = false;
    this.onChange = this.onChange.bind(this);
    this.onAddOccurrence = this.onAddOccurrence.bind(this);
    this.onDeleteOccurrence = this.onDeleteOccurrence.bind(this);
    this.onDragOccurrence = this.onDragOccurrence.bind(this);
    this.onDropOccurrence = this.onDropOccurrence.bind(this);
    this.onEditOccurrence = this.onEditOccurrence.bind(this);
    this.renderValue = this.renderValue.bind(this);
    const {
      values,
      field,
      path
    } = props;
    this.hideEditButton = groupHasAllInlineEditableChildren(field, path);
    this.state = {
      values: values,
      isCloning: false,
      clonedIndexes: []
    };
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      isCloning,
      values
    } = this.state;
    const {
      values: currentValues
    } = this.props;
    if (prevState.values.length < values.length && !isCloning && !this.hideEditButton) {
      this.onEditOccurrence(this.state.values.length - 1);
    }
    if (!prevState.isCloning && isCloning) {
      this.setState({
        isCloning: false
      });
    }
    if (!isEqual(prevProps.values, currentValues) && !isEqual(values, currentValues)) {
      this.setState({
        values: currentValues || []
      });
    }
  }
  get hasNoOccurrences() {
    const {
      values
    } = this.state;
    return !(values && values.length);
  }
  get hasMinOccurrences() {
    const {
      values
    } = this.state;
    const {
      min: minOccurrences
    } = this.props.field.occurrence;
    return values && minOccurrences && values.length <= minOccurrences;
  }
  get hasMaxOccurrences() {
    const {
      values
    } = this.state;
    const {
      max: maxOccurrences
    } = this.props.field.occurrence;
    return values && maxOccurrences && values.length >= maxOccurrences;
  }
  get hasMaxFeaturedOccurrences() {
    const {
      featuredLimit
    } = this.props;
    const {
      values
    } = this.state;
    if (!featuredLimit) {
      return false;
    }
    const featuredCount = values.reduce((count, value) => {
      if (value && value[HUBSPOT_META_VALUE_KEY] && value[HUBSPOT_META_VALUE_KEY].occurrence && value[HUBSPOT_META_VALUE_KEY].occurrence.featured) {
        count += 1;
      }
      return count;
    }, 0);
    return featuredCount >= featuredLimit;
  }
  get seleniumKey() {
    return `${this.props.fieldKey.join('.')}:occurrences`;
  }
  onChange(values, persist = true) {
    const {
      onFieldChange,
      fieldKey
    } = this.props;
    this.setState({
      values
    });
    if (persist) {
      onFieldChange(fieldKey, values);
    }
  }
  onAddOccurrence() {
    const {
      featuredEnabled,
      field
    } = this.props;
    const {
      values = []
    } = this.state;
    let newValue = getNewOccurrenceValue(field);
    if (featuredEnabled && newValue) {
      newValue = Object.assign({}, newValue, {
        [HUBSPOT_META_VALUE_KEY]: {
          occurrence: {
            featured: false
          }
        }
      });
    }
    this.onChange(values.concat([newValue]));
  }
  onDeleteOccurrence(index) {
    const {
      validationErrorIndexes,
      fieldKey
    } = this.props;
    const {
      values
    } = this.state;
    if (validationErrorIndexes.includes(index)) {
      this.props.removeValidationError([...fieldKey, index]);
    }
    const valuesWithIndex = values.slice(0, index).concat(values.slice(index + 1));
    validationErrorIndexes.forEach(validationIndex => {
      if (validationIndex > index) {
        this.props.shiftValidationError(fieldKey, validationIndex, validationIndex - 1);
      }
    });
    this.onChange(valuesWithIndex);
  }
  onDragOccurrence(oldIndex, newIndex) {
    const {
      values
    } = this.state;
    const value = values[oldIndex];
    let reorderedValues = values.slice(0, oldIndex).concat(values.slice(oldIndex + 1));
    reorderedValues = reorderedValues.slice(0, newIndex).concat(value, reorderedValues.slice(newIndex));
    this.onChange(reorderedValues, false);
  }
  onDropOccurrence() {
    this.onChange(this.state.values);
  }
  onEditOccurrence(index) {
    const {
      setFieldKey,
      fieldKey
    } = this.props;
    if (setFieldKey) setFieldKey(fieldKey.concat(index));
  }
  toggleFeatureOccurrence(index, featured) {
    const {
      values
    } = this.state;
    const newValues = [...values];
    newValues[index] = Object.assign({}, newValues[index], {
      [HUBSPOT_META_VALUE_KEY]: {
        occurrence: {
          featured: !featured
        }
      }
    });
    this.onChange(newValues);
  }
  onDuplicateOccurrence(index) {
    const {
      featuredEnabled,
      validationErrorIndexes,
      fieldKey
    } = this.props;
    const {
      values
    } = this.state;
    const newIndex = values.length;
    let newValue = values[index];
    if (featuredEnabled && newValue) {
      newValue = Object.assign({}, newValue, {
        [HUBSPOT_META_VALUE_KEY]: {
          occurrence: {
            featured: false
          }
        }
      });
    }
    if (validationErrorIndexes.includes(`${index}`)) {
      this.props.addValidationError([...fieldKey, newIndex]);
    }
    this.setState({
      isCloning: true,
      clonedIndexes: this.state.clonedIndexes.concat([newIndex])
    });
    this.onChange(values.concat([newValue]));
  }
  renderAddButton() {
    const {
      disabled,
      readOnly
    } = this.props;
    if (disabled || readOnly || this.hasNoOccurrences || this.hasMaxOccurrences) {
      return null;
    }
    return /*#__PURE__*/_jsx(Button, {
      "data-selenium": `${this.seleniumKey}:add-button`,
      onClick: this.onAddOccurrence,
      use: "link",
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('addLink')
        })
      })
    });
  }
  renderDisabledAlert() {
    const {
      disabled,
      field
    } = this.props;
    const {
      disabled_controls
    } = field;
    if (!disabled || !disabled_controls) return null;
    return /*#__PURE__*/_jsx(Alert, {
      type: "tip",
      marginBlockEnd: "300",
      children: /*#__PURE__*/_jsx(SanitizedLabel, {
        label: disabled_controls.message,
        useStrict: true
      })
    });
  }
  renderValue(value, index) {
    const {
      disabled,
      featuredEnabled,
      field,
      fieldKey,
      resources,
      readOnly,
      validationErrorIndexes,
      reorderableOnly
    } = this.props;
    const deletable = !this.hasMinOccurrences;
    const fieldId = field.id || field.name;
    const featured = featuredEnabled && value && value[HUBSPOT_META_VALUE_KEY] && value[HUBSPOT_META_VALUE_KEY].occurrence && value[HUBSPOT_META_VALUE_KEY].occurrence.featured;
    const fieldLabel = /*#__PURE__*/_jsx(OccurrenceLabel, {
      defaultLabel: `${field.label} ${index + 1}`,
      field: field,
      resources: resources,
      value: value
    });
    return /*#__PURE__*/_jsx(OccurrencesTableRow, {
      cloned: this.state.clonedIndexes.includes(index),
      deletable: deletable,
      featured: featured,
      featuredEnabled: featuredEnabled,
      fieldId: fieldId,
      fieldKey: fieldKey,
      hasMaxFeaturedOccurrences: this.hasMaxFeaturedOccurrences,
      hasMaxOccurrences: this.hasMaxOccurrences,
      hideEditButton: this.hideEditButton,
      index: index,
      onDeleteOccurrence: () => this.onDeleteOccurrence(index),
      onDuplicateOccurrence: () => this.onDuplicateOccurrence(index),
      onEditOccurrence: () => this.onEditOccurrence(index),
      toggleFeatureOccurrence: () => this.toggleFeatureOccurrence(index, featured),
      onDragOccurrence: this.onDragOccurrence,
      onDropOccurrence: this.onDropOccurrence,
      readOnly: readOnly || disabled,
      reorderableOnly: reorderableOnly,
      hasValidationError: validationErrorIndexes.includes(index),
      value: fieldLabel
    }, `${fieldId}-${index}`);
  }
  renderValues() {
    const {
      disabled,
      field: {
        label: fieldLabel
      },
      fieldKey,
      readOnly
    } = this.props;
    const {
      values
    } = this.state;
    let valueRows = values.map(this.renderValue);
    if (this.hasNoOccurrences) {
      const addLink = /*#__PURE__*/_jsx(Button, {
        onClick: this.onAddOccurrence,
        "data-selenium": `${fieldKey.join('.')}:addFirst`,
        disabled: readOnly || disabled,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('noOccurrences.addLink')
        })
      });
      valueRows = /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsx(OccurrencesTableCell, {
          isEmpty: true,
          children: /*#__PURE__*/_jsx(Text, {
            textAlign: "center",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: i18nKey('noOccurrences.placeholder'),
              options: {
                fieldLabel,
                addLink
              }
            })
          })
        })
      });
    }
    return /*#__PURE__*/_jsx(Table, {
      "data-selenium": `${this.seleniumKey}:table`,
      flush: true,
      hover: false,
      responsive: false,
      children: /*#__PURE__*/_jsx("tbody", {
        children: valueRows
      })
    });
  }
  renderLabel() {
    const {
      field,
      fieldKey,
      LabelComponent,
      onFieldChange,
      onFieldReset,
      allowFieldReset,
      isDefaultValue,
      scrollToField
    } = this.props;
    const {
      label,
      type,
      inherited_value
    } = field;
    return /*#__PURE__*/_jsx(EditFormLabel, {
      allowFieldReset: allowFieldReset,
      fieldKey: fieldKey,
      fieldType: type,
      inheritedValue: inherited_value,
      isDefaultValue: isDefaultValue,
      isOccurrencesLabel: true,
      label: label,
      LabelComponent: LabelComponent,
      onFieldReset: onFieldReset || (() => {}),
      onFieldUnlink: () => {
        onFieldChange(fieldKey, field.default);
      },
      scrollToField: scrollToField
    });
  }
  render() {
    const {
      field,
      fieldKey,
      flush,
      reorderableOnly
    } = this.props;
    const {
      label,
      name,
      required,
      help_text: helpText,
      inline_help_text: inlineHelpText
    } = field;
    return /*#__PURE__*/_jsx(EditFormControl, {
      "data-field-key": fieldKey.join('.'),
      "aria-label": label || name,
      helpText: helpText,
      inlineHelpText: inlineHelpText,
      label: this.renderLabel(),
      labelItemRight: !reorderableOnly && this.renderAddButton(),
      required: required,
      withPadding: !flush,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [this.renderDisabledAlert(), this.renderValues()]
      })
    });
  }
}
OccurrencesTable.defaultProps = {
  fieldKey: [],
  values: [],
  readOnly: false
};
export default connector(OccurrencesTable);